//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { useInfiniteQuery } from '@tanstack/react-query';

import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import {
  IContentLibraryItemParams,
  IContentLibraryItemsResponse,
} from '@apis/content-library/contentLibraryApiModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { TWENTY_SECONDS_STALE_TIME } from '@queries/QueryOptionsConstants';
import { ErrorType, getErrorMessage } from '@utils/errorMessages';

export interface ContentItemsQueryParams {
  query: IContentLibraryItemParams;
}

export const useContentLibraryItemsInfiniteQuery = ({
  query,
}: ContentItemsQueryParams) => {
  const limit = query.limit ?? 12;

  const fetchContentLibraryItems = async (page = 0, signal?: AbortSignal) => {
    try {
      return await getContentLibraryItems(
        {
          ...query,
          limit,
          offset: page > 0 ? (page - 1) * limit : 0,
        },
        { signal },
      );
    } catch (error) {
      throw new Error(getErrorMessage(ErrorType.MEDIA_ITEMS_FETCH, error));
    }
  };

  return useInfiniteQuery<IContentLibraryItemsResponse, Error>({
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersMediaItems,
      query,
    ],
    queryFn: async ({ pageParam, signal }) =>
      await fetchContentLibraryItems(pageParam, signal),
    getNextPageParam: (last, pages) =>
      last.contentItems.length < limit ? undefined : pages.length + 1,
    staleTime: TWENTY_SECONDS_STALE_TIME,
    keepPreviousData: true,
  });
};
